import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, TrashIcon, PencilIcon, PuzzlePieceIcon, PlayIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { DefaultService } from '../generated-api';
import StatCardsCarousel from '../components/common/StatCardsCarousel';
import PageHeader from '../components/common/PageHeader';
import FilterButton from '../components/common/FilterButton';
import SearchButton from '../components/common/SearchButton';
import { useData } from '../contexts/DataContext';

const GameLibraryPage: React.FC = () => {
  const navigate = useNavigate();
  const { games, fetchGames, invalidateGames } = useData();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gamePlayCounts, setGamePlayCounts] = useState<Record<string, number>>({});
  const [playerCountFilter, setPlayerCountFilter] = useState<string>('ALL');
  const [ownerFilter, setOwnerFilter] = useState<string>('ALL');
  const [tagFilter, setTagFilter] = useState<string>('ALL');
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const loadData = async () => {
    try {
      setError(null);
      const gamesData = await fetchGames();
      
      // Load play counts for each game
      const playCounts: Record<string, number> = {};
      await Promise.all(
        gamesData.map(async (game) => {
          try {
            const plays = await DefaultService.getGamePlaysGamesGameIdPlaysGet({ gameId: game.id });
            playCounts[game.id] = plays.length;
          } catch (err) {
            console.warn(`Failed to load plays for game ${game.id}:`, err);
            playCounts[game.id] = 0;
          }
        })
      );
      setGamePlayCounts(playCounts);
    } catch (err) {
      setError('Failed to load games. Please try again.');
      console.error('Error loading games:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - only run on mount

  const handleDeleteGame = async (gameId: string) => {
    const playCount = gamePlayCounts[gameId] || 0;
    const confirmMessage = playCount > 0
      ? `Are you sure you want to delete this game? This will NOT delete the ${playCount} recorded play sessions, but players will no longer be able to record new sessions for this game.`
      : 'Are you sure you want to delete this game? This action cannot be undone.';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await DefaultService.deleteGameGamesGameIdDelete({ gameId: gameId });
      invalidateGames();
      await fetchGames(true); // Force refresh after deletion
      const { [gameId]: removed, ...remainingCounts } = gamePlayCounts;
      setGamePlayCounts(remainingCounts);
    } catch (err) {
      setError('Failed to delete game. Please try again.');
      console.error('Error deleting game:', err);
    }
  };

  // Apply filters and sorting
  const filteredAndSortedGames = games
    .filter(game => {
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const nameMatch = game.name.toLowerCase().includes(query);
        const descMatch = game.description?.toLowerCase().includes(query);
        const ownerMatch = game.owners?.some(o => o.toLowerCase().includes(query));
        const tagMatch = game.tags?.some(t => t.toLowerCase().includes(query));
        if (!nameMatch && !descMatch && !ownerMatch && !tagMatch) {
          return false;
        }
      }

      // Player count filter
      if (playerCountFilter !== 'ALL') {
        const playerCount = parseInt(playerCountFilter);
        if (playerCount < game.minTeams || playerCount > game.maxTeams) {
          return false;
        }
      }

      // Owner filter
      if (ownerFilter !== 'ALL') {
        if (!game.owners || !game.owners.includes(ownerFilter)) {
          return false;
        }
      }

      // Tag filter
      if (tagFilter !== 'ALL') {
        if (!game.tags || !game.tags.includes(tagFilter)) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  // Get all unique owners for the filter
  const allOwners = Array.from(new Set(games.flatMap(game => game.owners || []))).sort();
  
  // Get all unique tags for the filter
  const allTags = Array.from(new Set(games.flatMap(game => game.tags || []))).sort();
  
  // Get all unique player counts for the filter  
  const allPlayerCounts = Array.from(new Set(games.flatMap(game => {
    const counts = [];
    for (let i = game.minTeams; i <= game.maxTeams; i++) {
      counts.push(i);
    }
    return counts;
  }))).sort((a, b) => a - b);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <PageHeader className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-fluid-h1 font-bold text-heading">Game Library</h1>
          <p className="mt-2 text-body">
            Manage your collection of board games
          </p>
        </div>
        <Link
          data-track="game_new"
          to="/games/new"
          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 sm:justify-start px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          aria-label="Add game"
        >
          <PlusIcon className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Add Game</span>
        </Link>
      </PageHeader>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Library Statistics */}
      {games.length > 0 && (
        <StatCardsCarousel
          title="Library Statistics"
          stats={[
            {
              icon: PuzzlePieceIcon,
              value: games.length,
              label: 'Games in Library',
              color: 'blue',
            },
            {
              icon: PlayIcon,
              value: Object.values(gamePlayCounts).reduce((sum, count) => sum + count, 0),
              label: 'Total Play Sessions',
              color: 'green',
            },
            {
              icon: ArchiveBoxIcon,
              value: games.reduce((sum, game) => sum + (game.availableExtensions?.length || 0), 0),
              label: 'Total Extensions',
              color: 'purple',
            },
          ]}
        />
      )}

      {/* Filters */}
      {games.length > 0 && (
        <>
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <h2 className="text-fluid-h2 font-semibold text-heading">Game Library</h2>
              <div className="flex items-center gap-2">
                <SearchButton
                  isExpanded={searchExpanded}
                  onToggle={() => setSearchExpanded(!searchExpanded)}
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search games..."
                />
                <FilterButton
                  onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                  isExpanded={isFilterExpanded}
                  hasActiveFilters={!!(playerCountFilter !== 'ALL' || ownerFilter !== 'ALL' || tagFilter !== 'ALL')}
                />
              </div>
            </div>
            <p className="text-body text-sm mt-1">
              {searchQuery ? (
                <>Showing {filteredAndSortedGames.length} of {games.length} games</>
              ) : (
                <>Filter and manage your collection of board games</>
              )}
            </p>
          </div>
          <div className={`mb-8 bg-surface shadow rounded-lg p-6 ${isFilterExpanded ? 'block' : 'hidden'} md:block`}>
            <h3 className="text-lg font-medium text-heading mb-4">Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label htmlFor="playerCountFilter" className="block text-sm font-medium text-body mb-2">
                Player Count
              </label>
              <select
                id="playerCountFilter"
                value={playerCountFilter}
                onChange={(e) => setPlayerCountFilter(e.target.value)}
                className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="ALL">All Player Counts</option>
                {allPlayerCounts.map(count => (
                  <option key={count} value={count.toString()}>{count} players</option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="ownerFilter" className="block text-sm font-medium text-body mb-2">
                Owner
              </label>
              <select
                id="ownerFilter"
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
                className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="ALL">All Owners</option>
                {allOwners.map(owner => (
                  <option key={owner} value={owner}>{owner}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="tagFilter" className="block text-sm font-medium text-body mb-2">
                Tag
              </label>
              <select
                id="tagFilter"
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
                className="w-full border border-edge-input rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="ALL">All Tags</option>
                {allTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
          </div>
          
            {(playerCountFilter !== 'ALL' || ownerFilter !== 'ALL' || tagFilter !== 'ALL') && (
              <div className="mt-4 text-sm text-body">
                Showing {filteredAndSortedGames.length} of {games.length} games
              </div>
            )}
          </div>
        </>
      )}

      {/* Games Grid */}
      {filteredAndSortedGames.length === 0 && games.length === 0 ? (
        <div className="bg-surface shadow rounded-lg p-12 text-center">
          <div className="mx-auto h-12 w-12 text-disabled">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-heading">No games in library</h3>
          <p className="mt-1 text-sm text-muted">Get started by adding your first board game.</p>
          <div className="mt-6">
            <Link
              data-track="game_new"
              to="/games/new"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Game
            </Link>
          </div>
        </div>
      ) : filteredAndSortedGames.length === 0 ? (
        <div className="bg-surface shadow rounded-lg p-12 text-center">
          <div className="mx-auto h-12 w-12 text-disabled">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-heading">No games match your filters</h3>
          <p className="mt-1 text-sm text-muted">Try adjusting your player count or owner filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAndSortedGames.map((game) => (
            <div
              data-track="game_card"
              key={game.id}
              className="bg-surface shadow rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/games/${game.id}`)}
              data-testid={`game-card-${game.id}`}
            >
              {/* Game Image */}
              <div className="h-48 w-full bg-surface-skeleton flex items-center justify-center p-2">
                {game.imageUrl ? (
                  <img
                    src={game.imageUrl}
                    alt={`${game.name} cover`}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <div className="text-disabled">
                    <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <p className="text-xs text-center mt-1">No image</p>
                  </div>
                )}
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-medium text-heading truncate">
                      {game.name}
                    </h3>
                    {game.description && (
                      <p className="mt-1 text-sm text-body line-clamp-2">
                        {game.description}
                      </p>
                    )}
                  </div>
                  <div className="ml-4 -mt-2.5 -mr-2.5 sm:mt-0 sm:mr-0 flex-shrink-0 flex space-x-1">
                    <Link
                      data-track="game_card_edit"
                      to={`/games/${game.id}/edit`}
                      className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 rounded-md text-disabled hover:text-body"
                      title="Edit game"
                      aria-label={`Edit ${game.name}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <PencilIcon className="h-5 w-5" />
                    </Link>
                    <button
                      data-track="game_card_delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteGame(game.id);
                      }}
                      className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 rounded-md text-disabled hover:text-red-600"
                      title="Delete game"
                      aria-label={`Delete ${game.name}`}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted">Teams:</span>
                    <span className="text-heading">{game.minTeams}-{game.maxTeams}</span>
                  </div>

                  {game.owners && game.owners.length > 0 && (
                    <div className="flex items-start justify-between text-sm">
                      <span className="text-muted flex-shrink-0">Owners:</span>
                      <div className="text-right flex-1 ml-2">
                        {game.owners.length === 1 ? (
                          <span className="text-heading">{game.owners[0]}</span>
                        ) : (
                          <div className="flex flex-wrap justify-end gap-1">
                            {game.owners.map((owner, idx) => (
                              <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                                {owner}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted">Play Sessions:</span>
                    <span className="text-heading">{gamePlayCounts[game.id] || 0}</span>
                  </div>

                  {game.availableExtensions && game.availableExtensions.length > 0 && (
                    <div className="pt-2">
                      <p className="text-xs text-muted mb-1">Available Extensions:</p>
                      <div className="flex flex-wrap gap-1">
                        {game.availableExtensions.slice(0, 3).map((extension, idx) => (
                          <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            {extension}
                          </span>
                        ))}
                        {game.availableExtensions.length > 3 && (
                          <span className="text-xs text-muted">
                            +{game.availableExtensions.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {game.tags && game.tags.length > 0 && (
                    <div className="pt-2">
                      <p className="text-xs text-muted mb-1">Tags:</p>
                      <div className="flex flex-wrap gap-1">
                        {game.tags.slice(0, 5).map((tag, idx) => (
                          <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
                            {tag}
                          </span>
                        ))}
                        {game.tags.length > 5 && (
                          <span className="text-xs text-muted">
                            +{game.tags.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-edge">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted">
                      Added {formatDate(game.createdAt)}
                    </span>
                    <div className="flex space-x-2">
                      <Link
                        data-track="game_card_plays"
                        to={`/plays?gameId=${game.id}`}
                        className="inline-flex items-center min-h-[44px] -my-3 sm:min-h-0 sm:my-0 px-1 text-sm text-primary-700 hover:text-primary-900"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View Plays
                      </Link>
                      <Link
                        data-track="game_card_record_play"
                        to={`/plays/new?gameId=${game.id}`}
                        className="inline-flex items-center min-h-[44px] -my-3 sm:min-h-0 sm:my-0 px-1 text-sm text-green-700 hover:text-green-900"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Record Play
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
};

export default GameLibraryPage;